import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { io, Socket } from "socket.io-client";

import { useAppDispatch } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import {
	addRealtimeNotification,
	setUnreadCount,
} from "@/pages/Private/redux/notification/notification.slice";

import { Notification } from "../schema/notification";

// ── Socket event names ─────────────────────────────────────────────────
enum NotificationSocketEvent {
	JOIN_USER_ROOM = "notification-join-user-room",
	NEW = "notification-new",
	BADGE_UPDATE = "notification-badge-update",
	ERROR = "notification-error",
}

// ── Context ────────────────────────────────────────────────────────────
interface NotificationSocketContextType {
	socket: Socket | null;
	latestNotification: Notification | null;
}

const NotificationSocketContext = createContext<NotificationSocketContextType | undefined>(
	undefined
);

// ── Provider ───────────────────────────────────────────────────────────
export const NotificationSocketProvider = ({ children }: { children: React.ReactNode }) => {
	const [socket, setSocket] = useState<Socket | null>(null);
	const [latestNotification, setLatestNotification] = useState<Notification | null>(null);
	const dispatch = useAppDispatch();
	const auth = useSelector(authSelector);
	const token = auth?.token;
	const userId = auth?.user?.id;

	// Ref to avoid stale closure issues in socket event handlers
	const dispatchRef = useRef(dispatch);

	dispatchRef.current = dispatch;

	useEffect(() => {
		if (!token || !userId) {
			return;
		}

		const newSocket = io(import.meta.env.VITE_API_URL, {
			query: { token },
			reconnection: true,
			reconnectionAttempts: 5,
			reconnectionDelay: 2000,
			reconnectionDelayMax: 10000,
			timeout: 10000,
			transports: ["websocket"],
		});

		newSocket.on("connect", () => {
			console.info("[NotificationSocket] Connected");
			newSocket.emit(NotificationSocketEvent.JOIN_USER_ROOM, String(userId));
		});

		newSocket.on("connect_error", (error) => {
			console.warn("[NotificationSocket] Connection error:", error);
		});

		newSocket.on(NotificationSocketEvent.NEW, (notification: Notification) => {
			dispatchRef.current(addRealtimeNotification(notification));
			setLatestNotification(notification);
		});

		newSocket.on(
			NotificationSocketEvent.BADGE_UPDATE,
			({ unreadCount }: { unreadCount: number }) => {
				dispatchRef.current(setUnreadCount(unreadCount));
			}
		);

		newSocket.on(NotificationSocketEvent.ERROR, (message: string) => {
			console.error("[NotificationSocket] Error:", message);
		});

		newSocket.on("disconnect", (reason) => {
			console.warn("[NotificationSocket] Disconnected:", reason);
		});

		setSocket(newSocket);

		return () => {
			newSocket.disconnect();
		};
	}, [token, userId]);

	return (
		<NotificationSocketContext.Provider value={{ socket, latestNotification }}>
			{children}
		</NotificationSocketContext.Provider>
	);
};

// ── Hook ───────────────────────────────────────────────────────────────
export const useNotificationSocket = () => {
	const context = useContext(NotificationSocketContext);

	if (!context) {
		throw new Error("useNotificationSocket must be used within a NotificationSocketProvider");
	}

	return context;
};
